import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  LocationOn as LocationOnIcon,
  LocalShipping as LocalShippingIcon,
  Store as StoreIcon,
  Receipt as ReceiptIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShippingOutlined as ShippedIcon,
  Done as DeliveredIcon,
  RadioButtonUnchecked as PendingIcon,
} from '@mui/icons-material';
import {
  formatMarketplaceDateTime,
  getPackageStatusDisplayLabel,
  normalizeMarketplaceStatusKey,
  ORDER_FLOW_COLORS,
  resolveMarketplaceStatusFlowColor,
} from '../utils/marketplaceStatusLabels';

const TY_ORANGE = '#f57c00';
const TY_ORANGE_LIGHT = '#fff3e0';

interface TrendyolPackageHistory {
  createdDate: number | string;
  status: string;
}

interface TrendyolOrder {
  id: string;
  orderId: string;
  orderNumber: string;
  date: string;
  status: string;
  totalAmount: number;
  itemsTotal?: number;
  items: any[];
  shippingAddress?: any;
  billingAddress?: any;
  cargoInfo?: any;
  trackingNumber?: string;
  shippingCompany?: string;
  shipmentPackageStatus?: string;
  marketplace?: string;
  n11OrderId?: string;
  n11Status?: string;
  customerName?: string;
  customerEmail?: string;
  packageHistories?: TrendyolPackageHistory[];
  marketplaceLastModified?: string;
  marketplaceImportedAt?: string;
}

interface TrendyolOrderDetailsModalProps {
  open: boolean;
  onClose: () => void;
  order: TrendyolOrder | null;
  getStatusChip: (status: string) => React.ReactElement;
}

const normalizeAddress = (raw: any) => {
  if (!raw || typeof raw !== 'object') return null;
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = raw[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
    }
    return '';
  };
  const firstLast = `${pick('firstName')} ${pick('lastName')}`.trim();
  return {
    name: pick('fullName', 'name', 'recipientName') || firstLast,
    address: pick('address', 'fullAddress', 'address1', 'addressLine'),
    city: pick('city', 'cityName'),
    district: pick('district', 'town', 'townName', 'county'),
    neighborhood: pick('neighborhood', 'districtName'),
    phone: pick('phone', 'gsm', 'phoneNumber', 'alternatePhoneNumber'),
    postalCode: pick('postalCode', 'zipCode'),
    tcId: pick('tcId', 'identityNo', 'turkishIdentityNumber'),
    taxNumber: pick('taxNumber', 'taxId', 'vkn'),
    taxOffice: pick('taxOffice', 'taxHouse'),
    invoiceType: pick('invoiceType', 'companyName'),
  };
};

const deepFindString = (obj: any, keys: string[], depth = 0): string => {
  if (!obj || typeof obj !== 'object' || depth > 6) return '';
  for (const k of Object.keys(obj)) {
    if (keys.some((t) => t.toLowerCase() === k.toLowerCase())) {
      const v = obj[k];
      if (v !== undefined && v !== null && typeof v !== 'object' && String(v).trim() !== '') {
        return String(v);
      }
    }
  }
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === 'object') {
      const found = deepFindString(Array.isArray(v) ? { ...v } : v, keys, depth + 1);
      if (found) return found;
    }
  }
  return '';
};

const parseJsonField = (raw: any) => {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

const getPackageStatusColor = (status: string, done = true): string => {
  if (!done) return '#bdbdbd';
  return ORDER_FLOW_COLORS[resolveMarketplaceStatusFlowColor(status)].main;
};

const getPackageStatusIcon = (status: string, done = true) => {
  if (!done) return <PendingIcon sx={{ color: '#fff', fontSize: 18 }} />;
  switch (normalizeMarketplaceStatusKey(status)) {
    case 'shipped':
      return <ShippedIcon sx={{ color: '#fff', fontSize: 18 }} />;
    case 'delivered':
      return <DeliveredIcon sx={{ color: '#fff', fontSize: 18 }} />;
    case 'cancelled':
    case 'canceled':
      return <CancelIcon sx={{ color: '#fff', fontSize: 18 }} />;
    default:
      return <CheckCircleIcon sx={{ color: '#fff', fontSize: 18 }} />;
  }
};

const buildDerivedTimeline = (order: TrendyolOrder) => {
  const status = String(order.status || '').toLowerCase();
  const isCancelled = status === 'cancelled' || status === 'cancel_requested';
  const reachedConfirmed = ['confirmed', 'shipped', 'delivered'].includes(status);
  const reachedShipped = ['shipped', 'delivered'].includes(status);
  const reachedDelivered = status === 'delivered';

  if (isCancelled) {
    return [
      { status: 'Created', label: 'Sipariş Oluşturuldu', date: order.date, done: true },
      {
        status: 'Cancelled',
        label: status === 'cancel_requested' ? 'İptal Talebi Alındı' : 'Sipariş İptal Edildi',
        done: true,
      },
    ];
  }

  return [
    { status: 'Created', label: 'Sipariş Oluşturuldu', date: order.date, done: true },
    { status: 'Picking', label: 'Sipariş Onaylandı / Hazırlanıyor', done: reachedConfirmed },
    { status: 'Shipped', label: 'Sipariş Kargolandı', done: reachedShipped },
    { status: 'Delivered', label: 'Sipariş Teslim Edildi', done: reachedDelivered },
  ];
};

const TrendyolOrderDetailsModal: React.FC<TrendyolOrderDetailsModalProps> = ({
  open,
  onClose,
  order,
  getStatusChip,
}) => {
  const timeline = useMemo(() => {
    if (!order) return [];
    const histories = order.packageHistories?.filter((h) => h?.status);
    if (histories && histories.length > 0) {
      return histories.map((history) => ({
        status: history.status,
        label: getPackageStatusDisplayLabel(history.status),
        date: history.createdDate,
        done: true,
      }));
    }
    return buildDerivedTimeline(order);
  }, [order]);

  if (!order) return null;

  const shipping = normalizeAddress(parseJsonField(order.shippingAddress));
  const billing = normalizeAddress(parseJsonField(order.billingAddress));
  const itemsTotal = order.itemsTotal || 0;
  const shippingFee = Math.max(0, (order.totalAmount || 0) - itemsTotal);

  const parsedCargoInfo = parseJsonField(order.cargoInfo) || order.cargoInfo;
  const cargoCompany =
    order.shippingCompany ||
    deepFindString(parsedCargoInfo, ['cargoProviderName', 'cargoCompany', 'shipmentCompany']);
  const trackingNumber =
    order.trackingNumber ||
    deepFindString(parsedCargoInfo, ['cargoTrackingNumber', 'trackingNumber', 'trackingInfoCode']);
  const cargoBarcode =
    deepFindString(parsedCargoInfo, ['cargoSenderNumber', 'cargoBarcode', 'barcode']);
  const trackingLink =
    deepFindString(parsedCargoInfo, ['cargoTrackingLink', 'trackingUrl', 'trackingInfoUrl']);
  const hasCargo = Boolean(cargoCompany || trackingNumber || cargoBarcode || trackingLink);

  const renderAddressCard = (
    addr: ReturnType<typeof normalizeAddress>,
    title: string,
    icon: React.ReactNode,
    showInvoiceExtras = false
  ) => {
    if (!addr || (!addr.name && !addr.address && !addr.city)) return null;
    return (
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {icon} {title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {addr.name && <Typography variant="body2" fontWeight="medium">{addr.name}</Typography>}
            {addr.address && <Typography variant="body2">{addr.address}</Typography>}
            {(addr.neighborhood || addr.district || addr.city) && (
              <Typography variant="body2">
                {addr.neighborhood && addr.neighborhood !== addr.district ? `${addr.neighborhood}, ` : ''}
                {[addr.district, addr.city].filter(Boolean).join(', ')}
              </Typography>
            )}
            {addr.postalCode && <Typography variant="body2">Posta Kodu: {addr.postalCode}</Typography>}
            {addr.phone && <Typography variant="body2">Telefon: {addr.phone}</Typography>}
            {showInvoiceExtras && addr.taxNumber && (
              <Typography variant="body2" color="text.secondary">Vergi No: {addr.taxNumber}</Typography>
            )}
            {showInvoiceExtras && addr.taxOffice && (
              <Typography variant="body2" color="text.secondary">Vergi Dairesi: {addr.taxOffice}</Typography>
            )}
            {showInvoiceExtras && addr.tcId && (
              <Typography variant="body2" color="text.secondary">TC: {addr.tcId}</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: TY_ORANGE_LIGHT,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              border: `1px solid ${TY_ORANGE}`,
            }}
          >
            <StoreIcon sx={{ color: TY_ORANGE, mr: 0.5 }} />
            <Typography variant="caption" sx={{ color: TY_ORANGE, fontWeight: 'bold' }}>
              Trendyol
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6">
              Trendyol Sipariş Detayları - #{order.orderNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {order.customerName || 'Müşteri bilinmiyor'}
              {order.customerEmail ? ` - ${order.customerEmail}` : ''}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <CalendarTodayIcon sx={{ mr: 1, fontSize: 16 }} />
                  Trendyol Sipariş Bilgileri
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Trendyol Sipariş No:</Typography>
                    <Typography variant="body2" fontWeight="medium">{order.orderNumber}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Paket ID:</Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {order.n11OrderId || order.orderId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Sipariş Tarihi:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatMarketplaceDateTime(order.date)}
                    </Typography>
                  </Box>
                  {order.marketplaceLastModified && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Son Güncelleme:</Typography>
                      <Typography variant="body2">
                        {formatMarketplaceDateTime(order.marketplaceLastModified)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Sistem Durumu:</Typography>
                    {getStatusChip(order.status)}
                  </Box>
                  {(order.n11Status || order.shipmentPackageStatus) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Trendyol Durumu:</Typography>
                      <Chip
                        label={getPackageStatusDisplayLabel(order.shipmentPackageStatus || order.n11Status)}
                        size="small"
                        sx={{ bgcolor: TY_ORANGE_LIGHT, color: TY_ORANGE, fontWeight: 'bold' }}
                      />
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: 16 }} />
                  Fiyat Bilgileri
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Ürünler Toplamı:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ₺{itemsTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Kargo Ücreti:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ₺{shippingFee.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" fontWeight="bold">Toplam Tutar:</Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: TY_ORANGE }}>
                      ₺{(order.totalAmount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {hasCargo && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    <LocalShippingIcon sx={{ mr: 1, fontSize: 16 }} />
                    Kargo Bilgileri
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Kargo Firması:</Typography>
                      <Typography variant="body2" fontWeight="medium">{cargoCompany || 'Belirtilmemiş'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Takip Numarası:</Typography>
                      <Typography variant="body2" fontFamily="monospace">{trackingNumber || 'Yok'}</Typography>
                    </Box>
                    {cargoBarcode && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Kargo Kodu:</Typography>
                        <Typography variant="body2" fontFamily="monospace">{cargoBarcode}</Typography>
                      </Box>
                    )}
                    {trackingLink && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Takip Linki:</Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(trackingLink, '_blank')}
                          sx={{ fontSize: '0.75rem', color: TY_ORANGE, borderColor: TY_ORANGE }}
                        >
                          Takip Et
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <TimelineIcon sx={{ mr: 1, fontSize: 16 }} />
                  Paket Geçmişi
                </Typography>
                <List sx={{ p: 0 }}>
                  {timeline.map((step, index) => (
                    <ListItem key={`${step.status}-${index}`} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: getPackageStatusColor(step.status, step.done),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {getPackageStatusIcon(step.status, step.done)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ color: step.done ? 'inherit' : 'text.disabled' }}
                          >
                            {step.label}
                          </Typography>
                        }
                        secondary={
                          step.date && step.done ? (
                            <Typography variant="caption" color="text.secondary">
                              {formatMarketplaceDateTime(step.date)}
                            </Typography>
                          ) : undefined
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {shipping && (
            <Grid item xs={12} md={6}>
              {renderAddressCard(shipping, 'Teslimat Adresi', <LocationOnIcon sx={{ mr: 1, fontSize: 16 }} />)}
            </Grid>
          )}

          {billing && (
            <Grid item xs={12} md={6}>
              {renderAddressCard(
                billing,
                'Fatura Adresi',
                <ReceiptIcon sx={{ mr: 1, fontSize: 16 }} />,
                true
              )}
            </Grid>
          )}

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: 16 }} />
                  Trendyol Sipariş Ürünleri ({order.items?.length || 0} ürün)
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(order.items || []).map((item: any, index: number) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={item.image || item.productImage || item.imageUrl}
                          alt={item.name}
                          variant="rounded"
                          sx={{ width: 60, height: 60 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">{item.name || item.productName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Trendyol Stok Kodu: {item.stockCode || item.sku || item.n11StockCode || 'Yok'}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Trendyol Satır ID: {item.lineId || item.n11LineId || item.n11OrderLineId || 'Yok'}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                          <Typography variant="body2" fontWeight="medium">Adet: {item.quantity}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                          <Typography variant="body2" fontWeight="medium">
                            ₺{Number(item.price || item.unitPrice || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', color: TY_ORANGE }}>
                            Toplam: ₺
                            {(
                              Number(item.price || item.unitPrice || 0) * Number(item.quantity || 0)
                            ).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrendyolOrderDetailsModal;
